import { Button, Flex, Menu, MenuItem } from '@aws-amplify/ui-react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { useLocation, useNavigate } from 'react-router-dom';
import { TokenPagination } from '../components/TokenPagination';
import CustomList from '../custom-components/CustomList';
import { DecisionLevelSlideout } from '../custom-components/DecisionLevelSlideout';
import { DistrictSizeHistogram } from '../custom-components/DistrictSizeHistogram';
import { FunctionalGroupSlideout } from '../custom-components/FunctionalGroupSlideout';
import LoadingBars from '../custom-components/LoadingBars';
import { MarketThemeSlideout } from '../custom-components/MarketThemeSlideout';
import NothingHere from '../custom-components/NothingHere';
import { TagSelector } from '../custom-components/TagSelector';
import { GlobalContext } from '../global-context';
import { toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import {
  ACCESS_TYPES,
  BuyingGroupQuery,
  DECISION_LEVELS,
  FUNCTIONAL_GROUPS,
  MARKET_THEMES,
  parseBuyingGroupQuery,
  serializeBuyingGroupQuery,
} from '../utils/BuyingGroupFilters';
import { Entity, SortOptions } from '../utils/CustomTypes';
import General from '../utils/GeneralUtils';
import { legacyGroupedPeopleResponse, LegacyGroupedPeopleResponse, toLegacyPerson, personOrder } from '../utils/legacyPerson';
import { handleNewExport } from '../utils/newExportFeature';
import { convertQtoQuery } from '../utils/queryConverter';
import { TableFields } from '../utils/TableFields';
import { US_STATES } from '../utils/UsStates';
import './BuyingGroups.scss';

const PAGE_SIZE = 25;

const STATE_OPTIONS = US_STATES.map(state => ({ value: state, label: state }));

export function BuyingGroups() {
  const globalContext = useContext(GlobalContext);
  General.setTitle(document, 'Buying Groups', globalContext);

  const location = useLocation();
  const navigate = useNavigate();

  const [isMarketThemeSlideoutOpen, setIsMarketThemeSlideoutOpen] = useState(false);
  const [isFunctionalGroupSlideoutOpen, setIsFunctionalGroupSlideoutOpen] = useState(false);
  const [isDecisionLevelSlideoutOpen, setIsDecisionLevelSlideoutOpen] = useState(false);

  // Search state
  const [isLoading, setIsLoading] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [searchResponse, setSearchResponse] = useState<LegacyGroupedPeopleResponse | null>(null);
  const [nextPageToken, setNextPageToken] = useState('');
  /** The tokens of the pages seen of the current search (tokens[i] loads page i + 1), for Previous. */
  const pageTokens = useRef<{ search: string; tokens: string[] }>({ search: '', tokens: [''] });
  const [selectedRows, setSelectedRows] = useState<{ [key: string]: any }>({});
  const [loadingMoreAccountIds, setLoadingMoreAccountIds] = useState<Set<string>>(new Set());

  // Filter state (what user is editing in UI)
  const [filterQuery, setFilterQuery] = useState<BuyingGroupQuery>({});

  // Toggle between functional group and market theme view
  const [filterMode, setFilterMode] = useState<'functionalGroup' | 'marketTheme'>('functionalGroup');

  // Current search params (from URL - what was actually searched)
  const [currentSearchParams, setCurrentSearchParams] = useState<{
    q: string | null;
    page: number;
    /** The Go API's token for `page`; empty for the first page. */
    pageToken: string;
    sortBy: string;
    sortDir: 'asc' | 'desc';
  } | null>(null);

  // Parse URL and update currentSearchParams
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    const page = parseInt(params.get('page') || '1', 10);
    const pageToken = params.get('pageToken') || '';
    const sortBy = params.get('sortBy') || 'relevance';
    const sortDir = (params.get('sortDir') || 'desc') as 'asc' | 'desc';

    // Update filter UI to match URL
    setFilterQuery(parseBuyingGroupQuery(q));

    // Update search params (this triggers the search)
    setCurrentSearchParams({ q, page, pageToken, sortBy, sortDir });
  }, [location.search]);

  // Export search params
  const exportSearchParams = useMemo(() => ({
    pageSize: PAGE_SIZE,
    entity: 'contacts',
    q: currentSearchParams?.q || '',
  }), [currentSearchParams?.q]);

  // Handle new export
  const handleNewExportClick = useCallback(async (config: { type: 'selected' | 'all'; ids?: string[] }) => {
    const estimatedCount = config.type === 'selected' && config.ids?.length
      ? config.ids.length
      : searchResponse?.data?.total || 0;

    const exportId = await handleNewExport({
      ...config,
      entity: 'CONTACT',
      searchParams: exportSearchParams,
      estimatedCount,
    });
    navigate(`/exports/${exportId}/target`);
  }, [exportSearchParams, navigate, searchResponse]);

  // Load contacts
  async function loadContacts(searchParams: typeof currentSearchParams, isPagination: boolean = false) {
    if (!searchParams?.q) return;

    try {
      if (isPagination) {
        setIsPaginationLoading(true);
      } else {
        setIsLoading(true);
      }

      const search = `${searchParams.q}|${searchParams.sortBy}|${searchParams.sortDir}`;
      if (pageTokens.current.search !== search) {
        pageTokens.current = { search, tokens: [''] };
      }
      if (searchParams.pageToken) {
        pageTokens.current.tokens[searchParams.page - 1] = searchParams.pageToken;
      }
      const response = await eduleadV4.people.search({
        conditions: JSON.stringify(convertQtoQuery(searchParams.q)),
        source: 'BUYING_GROUPS',
        order_by: personOrder(searchParams.sortBy, searchParams.sortDir),
        page_size: PAGE_SIZE,
        page_token: searchParams.pageToken || undefined,
        group_by: 'account',
        people_per_group: 10,
      });
      const next = response.response.next_page_token ?? '';
      if (next) {
        pageTokens.current.tokens[searchParams.page] = next;
      }
      setNextPageToken(next);
      setSearchResponse(legacyGroupedPeopleResponse(response.response));
    } catch (err) {
      toastV4Error(err, 'Something went wrong loading contacts');
    } finally {
      setIsLoading(false);
      setIsPaginationLoading(false);
    }
  }

  // Trigger search when currentSearchParams changes (and has a query)
  useEffect(() => {
    if (!currentSearchParams?.q) return;
    const isPagination = !!searchResponse?.data?.data?.length;
    loadContacts(currentSearchParams, isPagination);
  }, [currentSearchParams]);

  // Handle filter change (just updates local state, doesn't search)
  const updateFilterQuery = useCallback((updates: Partial<BuyingGroupQuery>) => {
    setFilterQuery(prev => {
      const newQuery = { ...prev, ...updates };
      // Remove empty arrays
      Object.keys(newQuery).forEach(key => {
        const k = key as keyof BuyingGroupQuery;
        if (Array.isArray(newQuery[k]) && newQuery[k]!.length === 0) {
          delete newQuery[k];
        }
      });
      return newQuery;
    });
  }, []);

  // Handle search button click - navigates to URL with current filters
  function handleSearch() {
    const q = serializeBuyingGroupQuery(filterQuery);
    if (!q) return;

    const params = new URLSearchParams();
    params.set('q', q);
    params.set('page', '1');
    params.set('sortBy', 'relevance');
    params.set('sortDir', 'desc');
    setSelectedRows({});
    navigate(`?${params.toString()}`);
  }

  // Handle sort
  function onSort(sortOptions: SortOptions) {
    if (!currentSearchParams?.q) return;
    const params = new URLSearchParams();
    params.set('q', currentSearchParams.q);
    params.set('page', '1');
    params.set('sortBy', sortOptions.sortBy || 'relevance');
    params.set('sortDir', sortOptions.sortDir || 'desc');
    setSelectedRows({});
    navigate(`?${params.toString()}`);
  }

  // Handle pagination: Next and Previous keep the page's token in the URL, so a refresh or a shared
  // link keeps its place; a token not seen in this visit (a link to a later page) sends Previous to
  // page 1.
  function handlePageChange(newPageNumber: number) {
    if (newPageNumber < 1 || !currentSearchParams?.q) return;
    const known = newPageNumber === 1 ? '' : pageTokens.current.tokens[newPageNumber - 1];
    const token = newPageNumber === currentSearchParams.page + 1 ? nextPageToken : known;
    const params = new URLSearchParams();
    params.set('q', currentSearchParams.q);
    params.set('page', token === undefined ? '1' : String(newPageNumber));
    if (token) params.set('pageToken', token);
    params.set('sortBy', currentSearchParams.sortBy);
    params.set('sortDir', currentSearchParams.sortDir);
    navigate(`?${params.toString()}`);
  }

  // Load more contacts for a specific account
  const handleLoadMoreContacts = useCallback(async (accountId: string) => {
    if (!currentSearchParams?.q) return;

    try {
      setLoadingMoreAccountIds(prev => new Set(prev).add(accountId));

      // Convert the original q string to query array, add accountId filter
      const originalQuery = convertQtoQuery(currentSearchParams.q);
      const queryWithAccountId = [
        ...originalQuery,
        { field: 'accountId', operator: 'equals', value: accountId }
      ];

      const response = await eduleadV4.people.search({
        conditions: JSON.stringify(queryWithAccountId),
        order_by: personOrder(currentSearchParams.sortBy, currentSearchParams.sortDir),
        page_size: 100,
      });

      // Append new contacts to existing ones (deduplicated by id)
      const loadedContacts = response.data.map(toLegacyPerson);
      setSearchResponse((prev) => {
        if (!prev?.data?.data) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            data: prev.data.data.map((account) => {
              if (account.accountId === accountId) {
                const existingIds = new Set(account.contacts.map((c) => c.id));
                const newContacts = loadedContacts.filter((c) => !existingIds.has(c.id));
                return { ...account, contacts: [...account.contacts, ...newContacts] };
              }
              return account;
            }),
          },
        };
      });
    } catch (err) {
      toastV4Error(err, 'Something went wrong loading more contacts');
    } finally {
      setLoadingMoreAccountIds(prev => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }
  }, [currentSearchParams]);

  const hasSearched = !!currentSearchParams?.q;

  // Get table fields from environment for contact display
  const tableFields = useMemo(() => {
    return new TableFields(
      (globalContext.environment?.entities?.contact?.tableFields || []) as unknown as ConstructorParameters<typeof TableFields>[0]
    );
  }, [globalContext.environment?.entities?.contact?.tableFields]);

  // Render filters - reusable for both sidebar and big centered view
  const renderFilters = (size: 'default' | 'big' = 'default') => (
    <>
      {filterMode === 'functionalGroup' ? (
        <TagSelector
          title={
            <Menu
              size="small"
              trigger={
                <span className="buying-groups__mode-trigger desc-16">
                  Functional Group <FaChevronDown className="buying-groups__mode-caret" />
                </span>
              }
            >
              <MenuItem onClick={() => setFilterMode('functionalGroup')}>Functional Group</MenuItem>
              <MenuItem onClick={() => setFilterMode('marketTheme')}>Market Theme</MenuItem>
            </Menu>
          }
          options={FUNCTIONAL_GROUPS}
          selected={filterQuery.functionalGroups || []}
          onChange={(functionalGroups) => updateFilterQuery({ functionalGroups })}
          color="green"
          size={size}
          onInfoClick={() => setIsFunctionalGroupSlideoutOpen(true)}
        />
      ) : (
        <TagSelector
          title={
            <Menu
              size="small"
              trigger={
                <span className="buying-groups__mode-trigger desc-16">
                  Market Theme <FaChevronDown className="buying-groups__mode-caret" />
                </span>
              }
            >
              <MenuItem onClick={() => setFilterMode('functionalGroup')}>Functional Group</MenuItem>
              <MenuItem onClick={() => setFilterMode('marketTheme')}>Market Theme</MenuItem>
            </Menu>
          }
          options={MARKET_THEMES}
          selected={filterQuery.marketThemes || []}
          onChange={(marketThemes) => updateFilterQuery({ marketThemes })}
          color="purple"
          size={size}
          onInfoClick={() => setIsMarketThemeSlideoutOpen(true)}
        />
      )}
      <TagSelector
        title={<span className="desc-16">Geo</span>}
        options={STATE_OPTIONS}
        selected={filterQuery.states || []}
        onChange={(states) => updateFilterQuery({ states })}
        color="purple"
        size={size}
      />
      <TagSelector
        title={<span className="desc-16">Decision Level</span>}
        options={DECISION_LEVELS}
        selected={filterQuery.decisionLevels || []}
        onChange={(decisionLevels) => updateFilterQuery({ decisionLevels })}
        color="blue"
        size={size}
        onInfoClick={() => setIsDecisionLevelSlideoutOpen(true)}
      />
      <TagSelector
        title={<span className="desc-16">Access</span>}
        options={ACCESS_TYPES}
        selected={filterQuery.accessTypes || []}
        onChange={(accessTypes) => updateFilterQuery({ accessTypes })}
        color="blue"
        size={size}
      />
    </>
  );

  // Show big centered filters when user hasn't searched yet
  if (!hasSearched) {
    return (
      <div className="buying-groups">
        <div className="buying-groups__empty-state">
          <div className="buying-groups__empty-state-filters">
            {renderFilters('big')}
          </div>
          <div className="buying-groups__empty-state-button">
            <Button
              className="white width-200"
              onClick={handleSearch}
              isDisabled={Object.keys(filterQuery).length === 0}
              isLoading={isLoading}
              loadingText="Searching"
            >
              Search People
            </Button>
          </div>
        </div>

        <MarketThemeSlideout
          isOpen={isMarketThemeSlideoutOpen}
          onClose={() => setIsMarketThemeSlideoutOpen(false)}
        />
        <FunctionalGroupSlideout
          isOpen={isFunctionalGroupSlideoutOpen}
          onClose={() => setIsFunctionalGroupSlideoutOpen(false)}
        />
        <DecisionLevelSlideout
          isOpen={isDecisionLevelSlideoutOpen}
          onClose={() => setIsDecisionLevelSlideoutOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className="buying-groups">
      <Group orientation="horizontal" id="buying-groups-layout-v2">
        <Panel id="filters" defaultSize="400px" minSize="300px" maxSize="800px" className="buying-groups__filters-panel">
          <div className="buying-groups__filters">
            {renderFilters('default')}
          </div>
          <div className="buying-groups__search-button">
            <Button
              size="small"
              className="white width-200"
              onClick={handleSearch}
              isDisabled={Object.keys(filterQuery).length === 0}
              isLoading={isLoading}
              loadingText="Searching"
            >
              Search People
            </Button>
          </div>
        </Panel>
        <Separator className="buying-groups__resize-handle" />
        <Panel id="content" minSize="400px" className="buying-groups__content-panel">
          <div className="content content-1000">
              {/* District Size Histogram */}
              {searchResponse?.data?.districtSizeHistogram && (
                <div style={{ marginBottom: '24px' }}>
                  <DistrictSizeHistogram
                    data={searchResponse.data.districtSizeHistogram}
                    selectedMin={filterQuery.nrStudentsMin}
                    selectedMax={filterQuery.nrStudentsMax}
                    onChange={(min, max) => {
                      // Update filter and immediately search
                      const newQuery = { ...filterQuery, nrStudentsMin: min, nrStudentsMax: max };
                      const q = serializeBuyingGroupQuery(newQuery);
                      const params = new URLSearchParams();
                      params.set('q', q);
                      params.set('page', '1');
                      params.set('sortBy', currentSearchParams?.sortBy || 'relevance');
                      params.set('sortDir', currentSearchParams?.sortDir || 'desc');
                      setSelectedRows({});
                      navigate(`?${params.toString()}`);
                    }}
                  />
                </div>
              )}

              {isLoading && !isPaginationLoading ? (
                <LoadingBars numberOfRows={6} />
              ) : searchResponse?.data?.data?.length ? (
                <>
                  <CustomList
                    searchResponse={searchResponse}
                    isLoading={isLoading}
                    isPaginationLoading={isPaginationLoading}
                    isExpanded={false}
                    isExporting={false}
                    onSort={onSort}
                    onExport={() => {}}
                    onNewExport={handleNewExportClick}
                    onExpandToggle={() => {}}
                    showExpandButton={false}
                    showSubheader={false}
                    showRowActionButtons={false}
                    showMainInstitution={false}
                    showColumnVisibilityButton={false}
                    currentSort={{ sortBy: currentSearchParams?.sortBy, sortDir: currentSearchParams?.sortDir }}
                    tableFields={tableFields}
                    entity={Entity.CONTACT}
                    selectedRows={selectedRows}
                    onSelectionChange={setSelectedRows}
                    groupedData={searchResponse.data.data}
                    groupedTotalContacts={searchResponse?.data?.totalContacts}
                    groupedTotalAccounts={searchResponse?.data?.total}
                    onLoadMoreContacts={handleLoadMoreContacts}
                    loadingMoreAccountIds={loadingMoreAccountIds}
                  />
                  <Flex marginTop={30} marginBottom={50} justifyContent="center">
                    <TokenPagination
                      page={currentSearchParams?.page || 1}
                      pageSize={PAGE_SIZE}
                      shown={searchResponse.data.data.length}
                      total={searchResponse.data.total}
                      hasNext={nextPageToken !== ''}
                      isLoading={isPaginationLoading}
                      onPrevious={() => handlePageChange((currentSearchParams?.page || 1) - 1)}
                      onNext={() => handlePageChange((currentSearchParams?.page || 1) + 1)}
                    />
                  </Flex>
                </>
              ) : (
                <NothingHere text="No accounts found..." />
              )}
          </div>
        </Panel>
      </Group>

      <MarketThemeSlideout
        isOpen={isMarketThemeSlideoutOpen}
        onClose={() => setIsMarketThemeSlideoutOpen(false)}
      />
      <FunctionalGroupSlideout
        isOpen={isFunctionalGroupSlideoutOpen}
        onClose={() => setIsFunctionalGroupSlideoutOpen(false)}
      />
      <DecisionLevelSlideout
        isOpen={isDecisionLevelSlideoutOpen}
        onClose={() => setIsDecisionLevelSlideoutOpen(false)}
      />
    </div>
  );
}
